import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { api, getToken } from '../api';

const WS_URL = process.env.REACT_APP_API_URL
  ? process.env.REACT_APP_API_URL.replace('http', 'ws')
  : `${window.location.protocol.replace('http', 'ws')}//${window.location.host}/svc`;

export default function SuperAdminChatConversationScreen() {
  const navigate = useNavigate();
  const { userId } = useParams();
  const location = useLocation();
  const userName = location.state?.userName || 'Клиент';

  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);
  const bottomRef = useRef(null);
  const wsRef = useRef(null);

  useEffect(() => {
    api.chat.conversation(userId)
      .then(data => setMessages(data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [userId]);

  useEffect(() => {
    const token = getToken();
    if (!token) return;
    const ws = new WebSocket(`${WS_URL}/chat/ws/${token}`);
    wsRef.current = ws;
    ws.onmessage = (e) => {
      try {
        const msg = JSON.parse(e.data);
        if (String(msg.user_id) !== String(userId)) return;
        setMessages(prev => {
          if (prev.find(m => m.id === msg.id)) return prev;
          const tempIdx = prev.findIndex(m => String(m.id).startsWith('temp_') && m.text === msg.text);
          if (tempIdx !== -1) return prev.map((m, i) => i === tempIdx ? msg : m);
          return [...prev, msg];
        });
      } catch {}
    };
    ws.onerror = () => {};
    ws.onclose = () => {};
    return () => { ws.onclose = null; ws.close(); wsRef.current = null; };
  }, [userId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  const sendMessage = () => {
    const trimmed = text.trim();
    if (!trimmed) return;
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) { alert('Нет соединения'); return; }
    const tempId = `temp_${Date.now()}`;
    setMessages(prev => [...prev, { id: tempId, user_id: userId, text: trimmed, is_from_admin: true, created_at: new Date().toISOString() }]);
    setText('');
    ws.send(JSON.stringify({ text: trimmed, user_id: userId }));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)' }}>
      <div style={{ display: 'flex', alignItems: 'center', padding: '10px 16px', borderBottom: '1px solid var(--border)', background: 'var(--card)', gap: 12 }}>
        <button onClick={() => navigate(-1)} style={{ background: 'none', color: 'var(--primary)', fontSize: 22, cursor: 'pointer', padding: 0 }}>←</button>
        <div style={{ width: 36, height: 36, borderRadius: 18, background: 'rgba(26,109,181,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, color: 'var(--primary)' }}>
          {(userName?.[0] || '?').toUpperCase()}
        </div>
        <span style={{ fontWeight: 600, fontSize: 15 }}>{userName}</span>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '8px 16px' }}>
        {loading ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--text-light)' }}>Загрузка...</div>
        ) : messages.length === 0 ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--text-light)' }}>Нет сообщений</div>
        ) : messages.map(msg => {
          const isAdmin = msg.is_from_admin;
          return (
            <div key={msg.id} style={{ display: 'flex', justifyContent: isAdmin ? 'flex-end' : 'flex-start', marginBottom: 6 }}>
              <div style={{
                maxWidth: '78%', borderRadius: 16, padding: '8px 14px',
                background: isAdmin ? 'var(--primary)' : 'var(--card)',
                border: isAdmin ? 'none' : '1px solid var(--border)',
                borderBottomLeftRadius: isAdmin ? 16 : 4,
                borderBottomRightRadius: isAdmin ? 4 : 16,
              }}>
                {isAdmin && <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.8)', fontWeight: 700, marginBottom: 2 }}>Поддержка</div>}
                <div style={{ fontSize: 14, lineHeight: 1.5, color: isAdmin ? '#fff' : 'var(--text)', whiteSpace: 'pre-wrap' }}>{msg.text}</div>
                <div style={{ fontSize: 10, marginTop: 3, textAlign: 'right', color: isAdmin ? 'rgba(255,255,255,0.7)' : 'var(--text-light)' }}>
                  {new Date(msg.created_at).toLocaleTimeString('ru', { hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, padding: '8px 16px', borderTop: '1px solid var(--border)', background: 'var(--card)' }}>
        <textarea value={text} onChange={e => setText(e.target.value)} onKeyDown={handleKeyDown}
          placeholder="Ответить клиенту..." maxLength={1000} rows={1}
          style={{ flex: 1, minHeight: 40, maxHeight: 120, background: 'var(--input-bg)', border: '1px solid var(--border)', borderRadius: 20, padding: '10px 16px', fontSize: 14, color: 'var(--text)', outline: 'none', resize: 'none', fontFamily: 'inherit' }} />
        <button onClick={sendMessage} disabled={!text.trim()}
          onTouchEnd={e => { e.preventDefault(); sendMessage(); }}
          style={{ width: 40, height: 40, borderRadius: 20, background: 'var(--primary)', color: '#fff', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', opacity: !text.trim() ? 0.4 : 1, flexShrink: 0 }}>
          ➤
        </button>
      </div>
    </div>
  );
}
